/**
 * AutoUpdate — applies a new deploy silently on the user's next in-app
 * navigation, so nobody sits on a stale cached bundle and nobody has to click
 * a "new version → Reload" toast (that toast came back after every deploy and
 * on every focus, which got old fast).
 *
 * How it works: the service worker (registerType: 'prompt', no skipWaiting)
 * installs the new build but WAITS. <AutoUpdate/> registers the worker itself
 * (injectRegister: false; NOT the plugin's useRegisterSW, whose 'controlling'
 * listener reloads EVERY open tab the moment one tab activates the update) and
 * notes the waiting worker. <ApplyUpdateOnNavigate/> (inside the router) then
 * waits for the next route change — a moment the user is leaving the page
 * anyway — posts SKIP_WAITING and reloads onto the new URL. Other open tabs
 * are not reloaded when the new worker takes over; they reload on their own
 * next navigation. It holds off while a fire-and-forget submit is in flight or
 * its failure toast (with an in-memory Retry) is open, and while the split
 * view pane is open (reloading the window would reload the pane under the user).
 *
 * Tabs that stay on one page (Oct 2026): the update is ALSO applied when the tab
 * goes into the background (the user is looking elsewhere), and when it comes
 * back to the foreground we check for a deploy and apply it right away. Both
 * skip a page the user has typed on since opening it (we can't tell whether
 * that text was saved) — such a page still updates on its next navigation.
 *
 * For tabs left open all day, we poll `registration.update()` on a timer and
 * when the tab regains focus, so an open tab discovers a deploy without
 * needing a manual reload first. Signed-out users get the update immediately
 * on the login page (see LoginPage).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hasUnfinishedBackgroundWork } from './BackgroundSubmit';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

let waitingWorker: ServiceWorker | null = null; // a new build waiting to activate
let takenOverElsewhere = false; // another tab activated it; this tab still runs the old bundle
let reloadOnTakeover = false; // this tab asked the worker to activate
let typedOnThisPage = false; // user typed on the current page → don't reload it unasked
let applyWhenReadyUntil = 0; // back in the foreground: apply a deploy found before this time

/** Typing in a real field (not a search box) marks the page as possibly holding unsaved text. */
function onUserInput(e: Event) {
  const t = e.target as HTMLElement | null;
  if (t instanceof HTMLInputElement && t.type === 'search') return;
  typedOnThisPage = true;
}

/**
 * Switch to the waiting build now, if it's safe. `fromNavigation` = the user is
 * leaving the page anyway, so text typed on it doesn't block the update.
 * Returns true when a reload was started.
 */
function applyUpdate(fromNavigation: boolean): boolean {
  if (!waitingWorker && !takenOverElsewhere) return false;
  if (!fromNavigation && typedOnThisPage) return false;
  if (hasUnfinishedBackgroundWork() || document.querySelector('iframe[data-split-pane]')) return false;
  if (takenOverElsewhere) {
    window.location.reload();
    return true;
  }
  reloadOnTakeover = true;
  waitingWorker?.postMessage({ type: 'SKIP_WAITING' });
  waitingWorker = null;
  // Fallback in case the controllerchange path stalls.
  window.setTimeout(() => window.location.reload(), 1500);
  return true;
}

/** Registers the service worker and watches for deploys. Mount once, outside the router. */
export function AutoUpdate() {
  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
    const sw = navigator.serviceWorker;
    // No controller yet = first install (clientsClaim takes over), not an update.
    let controlled = !!sw.controller;
    let timer: number | undefined;
    let check: (() => void) | undefined;

    const onControllerChange = () => {
      if (reloadOnTakeover) window.location.reload();
      else if (controlled) takenOverElsewhere = true;
      controlled = !!sw.controller;
    };
    sw.addEventListener('controllerchange', onControllerChange);

    sw.register('/sw.js', { scope: '/' }).then((reg) => {
      const noteWaiting = () => {
        if (reg.waiting && sw.controller) waitingWorker = reg.waiting;
        // A deploy found right after the tab came back: apply it now.
        if (waitingWorker && document.visibilityState === 'visible' && Date.now() < applyWhenReadyUntil) {
          applyWhenReadyUntil = 0;
          applyUpdate(false);
        }
      };
      noteWaiting();
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        nw?.addEventListener('statechange', () => {
          if (nw.state === 'installed') noteWaiting();
        });
      });
      check = () => {
        // Don't poll while offline or mid-install; ignore transient failures.
        if (reg.installing || !navigator.onLine) return;
        reg.update().catch(() => undefined);
      };
      timer = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
      window.addEventListener('focus', check);
    }).catch(() => undefined);

    // Background → apply a waiting build while nobody is looking.
    // Foreground → apply a waiting build, else check and apply one found in the next 20 s.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        applyUpdate(false);
        return;
      }
      if (applyUpdate(false)) return;
      applyWhenReadyUntil = Date.now() + 20_000;
      check?.();
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('input', onUserInput, true);

    return () => {
      sw.removeEventListener('controllerchange', onControllerChange);
      window.clearInterval(timer);
      if (check) window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('input', onUserInput, true);
    };
  }, []);

  return null;
}

/** Applies a waiting update on the next route change. Mount once, inside the router. */
export function ApplyUpdateOnNavigate() {
  const { pathname } = useLocation();
  const lastPath = useRef(pathname);

  useEffect(() => {
    if (pathname === lastPath.current) return;
    lastPath.current = pathname;
    // A new page: nothing typed on it yet.
    typedOnThisPage = false;
    applyUpdate(true);
  }, [pathname]);

  return null;
}
